import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, InputGroup } from 'react-bootstrap';
import _ from 'lodash';

import OptionsHelper from '../../lib/OptionsHelper';
import FormInput from '../../components/FormInput/FormInput';
import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { getListState, saveListState } from '../../lib/ListStateHelper';

import { useAlertStore } from '../../store/providers/Alert';
import { useDownloadInstancesStore } from '../../store/providers/DownloadInstances';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './DownloadInstancesSchema';

const DocumentSetupList = React.lazy(() => import('../../components/Lists/DocumentSetupList/DocumentSetupList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const DownloadInstancesModal = React.lazy(() => import('./DownloadInstancesModal'));

function DownloadInstances() {    
    const dispatch = useDispatch();

    const listKey = '/downloads';
    const savedListState = getListState(listKey) || {};

    const { addAlert } = useAlertStore();
    const { selectRecord, listRecords, listfranchisedownloadtypes, data, clearData } = useDownloadInstancesStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_CONTENT_DOWNLOADS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [ selectedFranchiseDownloadType, setSelectedFranchiseDownloadType ] = useState(savedListState.downloadTypeId || '');
    const [listfranchisedownloadtypesData, setListfranchisedownloadtypesData] = useState([]);
    const [listFranchiseDownloadTypeOptions, SetListFranchiseDownloadTypeOptions] = useState([]);

    const loadRecords = () => {
        listRecords(
            selectedFranchiseDownloadType
                ? {
                    franchiseDownloadTypeId:
                        selectedFranchiseDownloadType
                }
            : {}
        );
    };

    const selectedHandler = (downloadItem) => {
        setIsEditing(true);
        selectRecord({ Id: downloadItem.id });
    };

    const refreshedHandler = () => {
        loadRecords();
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        loadRecords();
    };

    const moreFilters = (listFranchiseDownloadTypeOptions) => {
        return (
            <>
                <div style={{ paddingLeft: '1%' }}>
                    <InputGroup style={{ width: '270px' }}>
                        <InputGroup.Addon>Download Type:</InputGroup.Addon>
                        <FormInput id="downloadTypeId"
                            type="select"
                            required={false}
                            labelText=''
                            errorMessage={''}
                            changed={(id, value) => changedHandler(id, value)}
                            value={selectedFranchiseDownloadType}
                        >
                            {listFranchiseDownloadTypeOptions}
                        </FormInput>
                    </InputGroup>
                </div>
            </>
        )
    }

    const changedHandler = (id, value) => {
        if (id !== 'downloadTypeId') {
            return;
        }

        const downloadTypeId = value
            ? parseInt(value, 10)
            : '';

        setSelectedFranchiseDownloadType(downloadTypeId);

        saveListState(listKey, {
            downloadTypeId,
            page: 1
        });

        listRecords(
            downloadTypeId
                ? { franchiseDownloadTypeId: downloadTypeId }
                : {}
        );
    };
  
    useEffect(() => {
        if (!_.isEmpty(listfranchisedownloadtypesData)) {
            const options = listfranchisedownloadtypesData && listfranchisedownloadtypesData.map(category => {
                return {
                    id: category.id,
                    name: category.name,
                    description: ''
                }
            });
            SetListFranchiseDownloadTypeOptions(OptionsHelper({ list: options, defaultOptions: [['', 'All']], includeBlank: false }));
        }
    }, [listfranchisedownloadtypesData]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {            
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            if (data.status === 'success' && data.scope === 'listfranchisedownloadtypes') {
                setListfranchisedownloadtypesData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'Downloads', text: 'Downloads' }));
        listRecords(
            selectedFranchiseDownloadType
                ? {
                    franchiseDownloadTypeId:
                        selectedFranchiseDownloadType
                }
                : {}
        );
        listfranchisedownloadtypes();

        setIsMounted(true);
    }, [
        hasPermission,
        isMounted,
        selectedFranchiseDownloadType,
        listRecords,
        listfranchisedownloadtypes,
        dispatch
    ]);

    return (
        <>
            <SideMenu activeKey="Downloads" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading Downloads..." />}>
                        {
                            isEditing && <DownloadInstancesModal
                                show={isEditing}
                                canEdit={canEdit}
                                title='Edit Download'
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Downloads" />
                            <Panel>
                                <Panel.Body>
                                     <DocumentSetupList
                                        listKey={listKey}
                                        compType="Download"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        hideAdd={true}
                                        hideDelete={true}
                                        moreFilters={moreFilters(listFranchiseDownloadTypeOptions)}
                                        selected={(activityPlan) => selectedHandler(activityPlan)}
                                        refreshed={() => refreshedHandler()}
                                        permissionEdit={canEdit}
                                    />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(DownloadInstances));
